import { useState, useTransition } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { PartyDetail } from "@/lib/types";
import { fmtDateTime } from "@/lib/dates";
import { Glyph, Modal, ModalForm } from "@/features/warehouse/WarehouseParts";
import { CARRIERS, type Carrier } from "@/features/warehouseOrders/warehouseOrders.api";
import { listPortalCarriers, portalCarrierKeys, savePortalCarriers, type PortalCarrier } from "./partnerUsers.api";

// Eigen dragers on the tab Portaal (docs/partner-portal.md §19; Camiel, 22-10-2026): the carriers a customer had in
// the TALIX Portal under its own names — A. van den Broek's "Blauwe Tosca Pallet", "H1 Pallets" — each counted as one
// of the portal's fixed carriers, which gives its loading metres and pallet places. The customer picks them first when
// entering an order, the fixed list below them; the name goes into the consignment's description.

const NAME_MAX = 60;
const CARRIERS_MAX = 30;

interface Row {
  key: string;
  id?: string;
  name: string;
  carrier: Carrier;
}

let rowKeys = 0;
const rowKey = () => `r${++rowKeys}`;

/** The one row on the tab: what the customer has of its own, and Beheren. */
export function PortalCarriersSetting({ party }: { party: PartyDetail }) {
  const { t } = useTranslation();
  const query = useQuery({ queryKey: portalCarrierKeys.byParty(party.id), queryFn: () => listPortalCarriers(party.id) });
  const [open, setOpen] = useState(false);
  const carriers = query.data?.carriers ?? [];
  return (
    <div className="wl-hstack" style={{ marginBottom: "var(--space-3)", gap: "var(--space-3)", flexWrap: "wrap", alignItems: "center" }}>
      <span className="t-body-sm" style={{ fontWeight: "var(--fw-semibold)" }}>
        {t("portal.carriers")}
      </span>
      <span className="t-body-sm t-muted">
        {query.isLoading
          ? "…"
          : carriers.length === 0
            ? t("portal.carriersNone")
            : carriers.map((row) => t("portal.carriersAsShort", { name: row.name, carrier: t(`portal.carrierAs.${row.carrier}` as never) })).join(" · ")}
      </span>
      <button type="button" className="wl-btn wl-btn--secondary wl-btn--sm" disabled={!query.data} onClick={() => setOpen(true)}>
        {t("portal.carriersManage")}
      </button>
      {open && query.data && <PortalCarriersModal party={party} carriers={query.data.carriers} onClose={() => setOpen(false)} />}
    </div>
  );
}

/** The latest change of the list: who and when, or null when nobody changed any yet. */
function lastChange(carriers: PortalCarrier[]): PortalCarrier | null {
  return carriers.reduce<PortalCarrier | null>((last, row) => (row.changedAt && (!last?.changedAt || row.changedAt > last.changedAt) ? row : last), null);
}

function PortalCarriersModal({ party, carriers, onClose }: { party: PartyDetail; carriers: PortalCarrier[]; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<Row[]>(() => carriers.map((row) => ({ key: rowKey(), id: row.id, name: row.name, carrier: row.carrier })));
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const names = rows.map((row) => row.name.trim().toLocaleLowerCase("nl"));
  const duplicate = new Set(names.filter((name, index) => name && names.indexOf(name) !== index));
  const ready = rows.every((row) => row.name.trim().length > 0) && duplicate.size === 0;
  const last = lastChange(carriers);

  const setRow = (key: string, patch: Partial<Row>) => setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  function submit() {
    setError(null);
    startTransition(async () => {
      try {
        const saved = await savePortalCarriers(
          party.id,
          rows.map((row) => ({ ...(row.id ? { id: row.id } : {}), name: row.name.trim(), carrier: row.carrier })),
        );
        // A fetch that started before the save (the window got focus with the click) would put the old list back.
        await queryClient.cancelQueries({ queryKey: portalCarrierKeys.byParty(party.id) });
        queryClient.setQueryData(portalCarrierKeys.byParty(party.id), saved);
        onClose();
      } catch (err) {
        setError(err instanceof Error ? err.message : t("common.errorGeneric"));
      }
    });
  }

  return (
    <Modal title={t("portal.carriersTitle", { party: party.name })} onClose={onClose} wide>
      <ModalForm onSubmit={submit} onCancel={onClose} error={error} isPending={isPending} submitLabel={t("portal.carriersSave")} disabled={!ready}>
        <p className="t-body-sm t-muted" style={{ margin: 0 }}>
          {t("portal.carriersExplain")}
        </p>
        {rows.length > 0 && (
          <div className="wl-vstack" style={{ gap: "var(--space-2)", alignItems: "stretch" }}>
            <div className="wl-hstack t-caption t-muted" style={{ gap: "var(--space-3)" }} aria-hidden="true">
              <span style={{ flex: "1 1 0" }}>{t("portal.carriersName")}</span>
              <span style={{ flex: "1 1 0" }}>{t("portal.carriersAs")}</span>
              <span style={{ width: "var(--control-height-md)" }} />
            </div>
            {rows.map((row) => {
              const twice = duplicate.has(row.name.trim().toLocaleLowerCase("nl"));
              return (
                <div key={row.key} className="wl-hstack" style={{ gap: "var(--space-3)", alignItems: "center" }}>
                  <input
                    className="wl-input"
                    style={{ flex: "1 1 0" }}
                    aria-label={t("portal.carriersName")}
                    aria-invalid={twice || undefined}
                    value={row.name}
                    maxLength={NAME_MAX}
                    autoFocus={!row.id && row.name === ""}
                    onChange={(e) => setRow(row.key, { name: e.target.value })}
                  />
                  <select
                    className="wl-select"
                    style={{ flex: "1 1 0" }}
                    aria-label={t("portal.carriersAs")}
                    value={row.carrier}
                    onChange={(e) => setRow(row.key, { carrier: e.target.value as Carrier })}
                  >
                    {CARRIERS.map((carrier) => (
                      <option key={carrier} value={carrier}>
                        {t(`portal.carrierAs.${carrier}` as never)}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="wl-btn wl-btn--ghost wl-btn--icon"
                    aria-label={t("portal.carriersRemove", { name: row.name.trim() || t("portal.carriersName") })}
                    onClick={() => setRows((prev) => prev.filter((other) => other.key !== row.key))}
                  >
                    <Glyph name="close" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        {duplicate.size > 0 && (
          <span className="t-body-sm" style={{ color: "var(--text-danger)" }} role="alert">
            {t("portal.carriersDuplicate")}
          </span>
        )}
        <div className="wl-hstack" style={{ gap: "var(--space-3)", alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            className="wl-btn wl-btn--secondary wl-btn--sm"
            disabled={rows.length >= CARRIERS_MAX}
            onClick={() => setRows((prev) => [...prev, { key: rowKey(), name: "", carrier: "europallet" }])}
          >
            <Glyph name="plus" />
            {t("portal.carriersAdd")}
          </button>
          <span className="wl-spacer" />
          {last?.changedAt && (
            <span className="t-caption t-muted">
              {last.changedBy
                ? t("portal.carriersChangedBy", { date: fmtDateTime(new Date(last.changedAt)), name: last.changedBy.name })
                : t("portal.carriersChanged", { date: fmtDateTime(new Date(last.changedAt)) })}
            </span>
          )}
        </div>
      </ModalForm>
    </Modal>
  );
}
